import * as React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { useTheme } from '../../../../../theme';
import { serviceForMultipleSegments } from '../../../hooks';
import { Baggage } from '../../../../../BaggageService/types';
import { SelectedUpgrade } from '../../../types';
import { getBaggageCalculatedRestrictionCount, getPassengerBaggageState } from '../../../store/selector';
import { getMinBaggageCount, updateSelectedItems } from '../../../utils';
import BaggageCounter from '../../../../BaggageCounter';
import { Tooltip } from '../../../../../BaseComponents';
import { Passenger } from '../../../../types';

interface Props {
	className?: string;
	passenger: Passenger;
	baggages: Baggage[]; // Baggage services with same rfics codes
	onChange: (items: SelectedUpgrade[]) => void;
	isIncreaseDisabled?: boolean;
	segmentIds: string[];
	segmentId?: string;
}

const PassengerBaggageCounter: React.FC<Props> = ({
	className,
	passenger,
	baggages,
	onChange,
	isIncreaseDisabled: increaseDisabled,
	segmentIds,
	segmentId
}) => {
	const { t } = useTranslation('IncludedBaggage');
	const theme = useTheme('UpgradeBaggageDialog').BaggagePassenger;

	const [tooltipOpen, setTooltipOpen] = React.useState(false);

	const baggage = baggages[0];
	const passengerId = passenger.id;

	const { items, selectedItems, selectedCount, confirmedCount } = useSelector(
		getPassengerBaggageState({ passengerId, baggage: baggages, segmentIds, segmentId })
	);

	const { maxCount, selectedCount: baggageSelectedCount, isExistRestriction } = useSelector(
		getBaggageCalculatedRestrictionCount({ baggage, passengerId, segmentId, segmentIds })
	);

	const isConfirmed = React.useMemo(() => selectedCount === confirmedCount, [selectedCount, confirmedCount]);

	const isIncreaseDisabled = React.useMemo(() => {
		const selectedBaggageCount = isExistRestriction ? baggageSelectedCount : selectedCount;

		return maxCount <= selectedBaggageCount || increaseDisabled;
	}, [maxCount, isExistRestriction, baggageSelectedCount, selectedCount, increaseDisabled, isConfirmed]);

	const minCount = React.useMemo(() => getMinBaggageCount(passenger, baggages, segmentId), [passenger]);

	const multipleSegments = React.useMemo(() => serviceForMultipleSegments(baggages[0]), [baggages]);

	const onChangeHandler = (count: number) => {
		const newItems = updateSelectedItems(selectedCount, count, items, selectedItems);

		if (newItems) {
			onChange(newItems);
		}

		if (multipleSegments) {
			setTooltipOpen(true);

			setTimeout(() => setTooltipOpen?.(false), 700);
		}
	};

	return (
		<div
			className={cn(className, theme.counter_wrapper)}
			data-test-id="baggage-counter"
			data-passenger-id={passengerId}
			data-segment-id={segmentId}
		>
			<Tooltip
				title={t('Added for all flights')}
				disableHoverListener={!multipleSegments}
				disableTouchListener={!multipleSegments}
				open={tooltipOpen}
			>
				<div>
					<BaggageCounter
						max={maxCount}
						min={minCount}
						value={selectedCount}
						isIncreaseDisabled={isIncreaseDisabled}
						isDecreaseDisabled={isConfirmed}
						onChange={onChangeHandler}
					/>
				</div>
			</Tooltip>
		</div>
	);
};

export default PassengerBaggageCounter;
